import type { AxiosError, AxiosResponse } from "axios";
import axios from "axios";

import { onToast } from "@components/Toast";

import { clearSession } from "@storage/session";
import { appTokenStorage } from "@storage/sessionStorage";

import { ROUTES } from "@configs/routes";

import { axiosBaseToysGate } from "./axios.base";
import { EHttpStatusCode } from "./httpStatusCodes";

const onFullFilled = <T extends IResponseData>(response: AxiosResponse<T>) => {
	const responseData = response.data;

	if (responseData.code && responseData.code !== EHttpStatusCode.OK) {
		throw responseData;
	}

	return responseData;
};

const onRejected = async ({ error, prefix, baseURL }: IOnRejected) => {
	let errorMessage = error?.message || error?.error || "Unknown error occurred";
	if (Array.isArray(error?.errors)) {
		const firstError = error.errors[0];
		errorMessage = firstError?.message || firstError?.name || firstError?.field;
	}
	const errorCode = error ? `Code: ${error.code}` : "";

	onToast({
		id: `${prefix}-error`,
		message: "Error",
		description: errorMessage,
	});

	console.error(`${prefix}: ${errorMessage} ${errorCode}`);

	const isToysGateRequest = Boolean(baseURL) && baseURL === axiosBaseToysGate.defaults.baseURL;
	if (error?.code === EHttpStatusCode.UNAUTHORIZED && !isToysGateRequest) {
		clearSession();
		window.location.href = ROUTES.LOGIN;
	}

	return Promise.reject(error);
};

const processResponse = async <T>({ response, prefix }: IProcessResponse<T>) => {
	try {
		// @ts-expect-error - response is of type AxiosResponse<T>
		return onFullFilled<T>(response);
	} catch (e) {
		return onRejected({ error: e as IResponseData, prefix, baseURL: response.config?.baseURL });
	}
};

export const processRequest = async <T>({ prefix, request }: IProcessRequest<T>) => {
	const BSS_TOKEN = appTokenStorage.getItem();

	try {
		const response = await request(BSS_TOKEN || null);
		return processResponse({ response, prefix });
	} catch (e) {
		if (axios.isCancel(e)) {
			throw e;
		}
		const eTyped = e as AxiosError;
		return onRejected({
			error: eTyped.response?.data as IResponseData,
			prefix,
			baseURL: eTyped.config?.baseURL ?? eTyped.response?.config?.baseURL,
		});
	}
};

interface IProcessRequest<T> extends IMainProps {
	request: (token: string | null) => Promise<AxiosResponse<T>>;
}

interface IProcessResponse<T> extends IMainProps {
	response: AxiosResponse<T>;
}

interface IOnRejected extends IMainProps {
	error?: IResponseData;
	baseURL?: string;
}

interface IMainProps {
	prefix: string;
}

export interface IResponseData {
	code?: EHttpStatusCode;
	message?: string;
	error?: string;
	errors?: Record<string, string>;
}
